import { Component, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { defaultsDeep } from "lodash";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { AppService } from "src/app/apps/app.service";
import { NotificationService } from "src/app/notifications/notification.service";
import { SessionService } from "src/app/session.service";
import { UiService } from "src/app/ui/ui.service";
import { addCommasToNumber } from "src/app/util/util.service";
//TODO REDEMPTIONS GRAPH AND REGION SELECT

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const MONTHS_FULL = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const DEFUALT_PIE_CHART_OPTIONS = {
  grid: {
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
  },
  tooltip: {
    trigger: "item",
  },
  series: [
    {
      type: "pie",
      radius: ["43%", "90%"],
      label: {
        show: true,
        position: "inside",
      },

      emphasis: {
        label: {
          show: true,
          fontSize: "20",
          fontWeight: "bold",
        },
      },
      labelLine: {
        show: false,
      },
    },
  ],
};

@Component({
  selector: "app-insights",
  templateUrl: "./insights.component.html",
  styleUrls: ["./insights.component.scss"],
})
export class InsightsComponent implements OnInit {
  filterForm!: FormGroup;
  installsChartOptions: any;
  mobileLocationChartOption: any;
  currentRedemption: any;
  redemptionChartOptions: any;
  installs: any;
  installsRange: any;
  installsType: any;
  locationAllowed: any;
  totalDeliveries!: number;
  deliveriesByCategory: any;
  categoryCharts: any;
  signalNames: any;
  signalForm!: FormGroup;
  filteredSignals: any;
  geofenceDeliveries: any;
  geofenceDeliveriesTotal!: number;
  deliveryGeofenceSortType!: string;
  private onComponentDestroy = new Subject();

  constructor(
    private route: ActivatedRoute,
    private SessionService: SessionService,
    private AppService: AppService,
    private NotificationService: NotificationService,
    private uiService: UiService,
  ) {}

  ngOnInit(): void {
    this.filterForm = new FormGroup({
      startDate: new FormControl(this.route.snapshot.data.start, [
        Validators.required,
      ]),
      endDate: new FormControl(this.route.snapshot.data.end, [
        Validators.required,
      ]),
    });

    this.signalForm = new FormGroup({
      signal: new FormControl(null),
      signalSearch: new FormControl(null),
    });

    this.signalNames = (
      this.route.snapshot.data.insights.signal_names ?? []
    ).sort((a: any, b: any) => {
      return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
    });

    this.filteredSignals = this.signalNames;

    this.signalForm.controls.signalSearch.valueChanges
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((value) => {
        if (value === null || value === undefined || value === "") {
          this.filteredSignals = this.signalNames;
        } else {
          this.filteredSignals = this.signalNames.filter((signal: any) => {
            return signal.name.toLowerCase().includes(value.toLowerCase());
          });
        }
      });

    this.signalForm.controls.signal.valueChanges
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((value) => {
        if (value) {
          this.uiService
            .waitForEvent(
              this.AppService.getInsightRedemptions(
                this.accountID,
                this.appID,
                value.id,
                value.type,
                this.filterForm.controls.startDate.value,
                this.filterForm.controls.endDate.value,
              ),
            )
            .pipe(takeUntil(this.onComponentDestroy))
            .subscribe((res) => {
              this.initRedemptionData(res);
            });
        }
      });

    this.initData(this.route.snapshot.data.insights);

    this.filterForm.valueChanges
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((change) => {
        if (this.filterForm.valid && change.startDate < change.endDate) {
          this.filterForm.controls.endDate.value.setHours(23, 59, 59);

          this.uiService
            .waitForEvent(
              this.AppService.getInsights(
                this.SessionService.currentAccountValue!.accountID,
                this.SessionService.currentAppValue!.id,
                change.startDate,
                this.filterForm.controls.endDate.value,
              ),
            )
            .pipe(takeUntil(this.onComponentDestroy))
            .subscribe(
              (insights) => {
                this.initData(insights);
              },
              (err) => {
                this.NotificationService.openErrorSnackBar(
                  "Error retrieving information",
                );
              },
            );

          if (this.signalForm.controls.signal.value) {
            this.uiService
              .waitForEvent(
                this.AppService.getInsightRedemptions(
                  this.accountID,
                  this.appID,
                  this.signalForm.controls.signal.value.id,
                  this.signalForm.controls.signal.value.type,
                  this.filterForm.controls.startDate.value,
                  this.filterForm.controls.endDate.value,
                ),
              )
              .pipe(takeUntil(this.onComponentDestroy))
              .subscribe((res) => {
                this.initRedemptionData(res);
              });
          }
        }
      });
  }

  ngOnDestroy() {
    this.onComponentDestroy.next();
    this.onComponentDestroy.complete();
  }

  initData(insights: any) {
    this.installs = Number.parseInt(insights.total_installs ?? 0);

    this.installsRange = insights.installs.installs_range;

    this.installsType = insights.installs.type;

    this.locationAllowed = Number.parseInt(insights.location_allowed ?? 0);

    this.totalDeliveries = Number.parseInt(insights.total_deliveries ?? 0);

    this.deliveriesByCategory = insights.deliveries_per_category;

    this.geofenceDeliveries = insights.geofence_deliveries;

    this.geofenceDeliveriesTotal = 0;

    for (let geofenceDelivery of this.geofenceDeliveries) {
      this.geofenceDeliveriesTotal =
        this.geofenceDeliveriesTotal + geofenceDelivery.deliveries;
    }

    this.deliveryGeofenceSortType = "high_to_low";
    this.sortDeliveriesByGeofences();

    let categoryChartCount = 0;

    this.categoryCharts = this.deliveriesByCategory.map((category: any) => {
      const deliveryChartObj = defaultsDeep(
        {},
        {
          series: [
            {
              name: category.category_name,
              type: "pie",
              radius: ["43%", "90%"],
              avoidLabelOverlap: true,
              label: {
                color: category.color,
                fontSize: "20",
                position: "center",
                formatter: () => {
                  return `${Math.round(
                    (Number.parseInt(category.signals) / this.totalDeliveries) *
                      100,
                  )}%`;
                },
              },
              labelLine: {
                show: false,
              },
              data: [
                {
                  value: Number.parseInt(category.signals),
                  itemStyle: {
                    color: category.color,
                  },
                },
                {
                  value:
                    this.totalDeliveries - Number.parseInt(category.signals),

                  itemStyle: {
                    color: "#F0F0F0",
                  },
                  emphasis: {
                    itemStyle: {
                      color: "#EAEAEA",
                    },
                  },
                },
              ],
            },
          ],
        },
      );

      categoryChartCount++;

      return { name: category.category_name, chartOptions: deliveryChartObj };
    });

    this.initInstallsChart();

    this.mobileLocationChartOption = defaultsDeep(
      {
        tooltip: {
          trigger: "item",
          formatter: (params: any, ticket: any, callback: any) => {
            return `<strong>${params.name}</strong>
            <br/>Percent: ${params.percent}%
            <br/>Total: ${addCommasToNumber(params.value)}
            `;
          },
        },
        series: [
          {
            name: "Mobile location",
            data: [
              {
                value: this.installs - this.locationAllowed,

                name: "No",
                label: {
                  color: "#AAAAAA",
                },
                itemStyle: {
                  color: "#F0F0F0",
                },
                emphasis: {
                  itemStyle: {
                    color: "#EAEAEA",
                  },
                },
              },
            ],
          },
        ],
      },
      DEFUALT_PIE_CHART_OPTIONS,
    );

    if (this.locationAllowed) {
      this.mobileLocationChartOption.series[0].data.push({
        value: this.locationAllowed,
        name: "Yes",
        label: {
          color: "#FFF",
        },
        itemStyle: {
          color: "#A1C700",
        },
      });
    }
  }

  sortDeliveriesByGeofences() {
    if (this.deliveryGeofenceSortType === "high_to_low") {
      this.geofenceDeliveries.sort((a: any, b: any) => {
        return b.deliveries - a.deliveries;
      });
    } else if (this.deliveryGeofenceSortType === "low_to_high") {
      this.geofenceDeliveries.sort((a: any, b: any) => {
        return a.deliveries - b.deliveries;
      });
    }
  }

  initRedemptionData(redemptionData: any) {
    this.redemptionChartOptions = {
      grid: {
        containLabel: true,
        left: 20,
        top: 20,
        right: 20,
        bottom: 20,
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
      },
      xAxis: {
        type: "time",
        axisLine: {
          show: true,
        },
        axisTick: {
          show: false,
        },
      },
      yAxis: {
        type: "value",
      },

      series: [
        {
          name: "Redemptions",
          data: redemptionData.map((redemptionRange: any) => {
            return [redemptionRange.dates, redemptionRange.redeems];
          }),

          type: "line",
          smooth: true,
          symbol: "circle",
          symbolSize: 10,
          lineStyle: {
            shadowOffsetX: -5,
            shadowColor: "rgba(0, 0, 0, 0.5)",
            shadowBlur: 10,
            shadowOffsetY: 10,
          },
        },
      ],
    };
  }

  get accountID() {
    return this.SessionService.currentAccountValue!.accountID;
  }

  get appID() {
    return this.SessionService.currentAppValue!.id;
  }

  onRedemptionSelected(row: any) {
    this.currentRedemption = row;
  }

  initInstallsChart() {
    let type: "Day" | "Week" | "Month";

    if (this.installsRange.length < 90) {
      type = "Day";
    } else if (this.installsRange.length < 180) {
      type = "Week";
    } else {
      type = "Month";
    }

    const installDataObj: {
      type: "Day" | "Week" | "Month";
      data: {
        date: Date;
        total: number;
      }[];
    } = { type, data: [] };

    if (type === "Day") {
      for (let dateRange of this.installsRange) {
        installDataObj.data.push({
          date: dateRange.install_date,
          total: dateRange.installs,
        });
      }
    } else if (type === "Week") {
      this.installsRange.sort((a: any, b: any) => {
        return (a.install_date as string).localeCompare(b.install_date);
      });

      const minDate: Date = new Date(this.installsRange[0].install_date);
      const maxDate = new Date(
        this.installsRange[this.installsRange.length - 1].install_date,
      );

      let currStartWeek = minDate;
      while (currStartWeek.getTime() < maxDate.getTime()) {
        const weekEnd = new Date(currStartWeek);

        weekEnd.setDate(currStartWeek.getDate() + 7);

        const weekObj = {
          weekStart: currStartWeek,
          weekEnd,
          total: 0,
        };

        const installsInRange = this.installsRange.filter((install: any) => {
          const date = new Date(install.install_date);
          return (
            date.getTime() >= weekObj.weekStart.getTime() &&
            date.getTime() < weekObj.weekEnd.getTime()
          );
        });

        installsInRange.forEach((install: any) => {
          weekObj.total = weekObj.total + install.installs;
        });

        installDataObj.data.push({
          date: weekObj.weekStart,
          total: weekObj.total,
        });

        currStartWeek = weekEnd;
      }
    } else if (type === "Month") {
      this.installsRange.sort((a: any, b: any) => {
        return (a.install_date as string).localeCompare(b.install_date);
      });

      const monthsArr = [];
      const minDate = new Date(this.installsRange[0].install_date);

      const maxDate = new Date(
        this.installsRange[this.installsRange.length - 1].install_date,
      );

      let currMonth = minDate;

      while (currMonth.getTime() < maxDate.getTime()) {
        const monthEnd = new Date(currMonth);
        monthEnd.setMonth(monthEnd.getMonth() + 1);
        monthEnd.setDate(1);
        monthEnd.setHours(0);
        monthEnd.setMinutes(0);
        monthEnd.setSeconds(0);
        monthEnd.setMilliseconds(0);

        const monthObj = {
          month: currMonth,
          total: 0,
        };

        const installsInRange = this.installsRange.filter((install: any) => {
          const date = new Date(install.install_date);
          return (
            date.getTime() >= monthObj.month.getTime() &&
            date.getTime() < monthEnd.getTime()
          );
        });

        installsInRange.forEach((install: any) => {
          monthObj.total = monthObj.total + install.installs;
        });

        installDataObj.data.push({
          date: monthObj.month,
          total: monthObj.total,
        });

        currMonth = monthEnd;
      }
    }

    this.installsChartOptions = {
      grid: {
        left: 10,
        top: 0,
        right: 10,
        bottom: 22,
      },

      xAxis: {
        type: "category",
        boundaryGap: false,

        axisLabel: {
          color: "#fff",
          hideOverlap: false,
          formatter: (d: any) => {
            return this.getXLabel(d, type);
          },
        },
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
      },
      yAxis: {
        type: "value",
        splitLine: {
          show: false,
        },
        show: false,
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
        formatter: (params: any, ticket: any, callback: any) => {
          return this.getTooltip(params, type, params[0].axisValue);
        },
      },
      series: [
        {
          data: installDataObj.data.map((data: any) => {
            return [data.date, data.total];
          }),
          showSymbol: false,

          label: {
            color: "#7dc4fd",
          },
          type: "bar",
          itemStyle: { color: "white", borderRadius: [5, 5, 0, 0] },
          large: true,
        },
      ],
    };
  }

  getXLabel(dateString: string, type: "Day" | "Week" | "Month") {
    const date = new Date(dateString);
    if (type === "Day") {
      return this.getDayLabel(date);
    } else if (type === "Week") {
      return this.getWeekLabel(date);
    } else if (type === "Month") {
      return this.getMonthLabel(date);
    }

    return "";
  }

  getDayLabel(date: Date) {
    if (date.getDate() === 1) {
      return MONTHS[date.getMonth()];
    }

    return date.getDate().toString();
  }

  getWeekLabel(date: Date) {
    const currentDate = date.getDate();
    if (currentDate < 7) {
      return MONTHS[date.getMonth()];
    }
    return `${date.getMonth()}/${date.getDate()}`;
  }

  getMonthLabel(date: Date) {
    const currentMonth = date.getMonth();
    if (currentMonth === 0) {
      return date.getFullYear().toString();
    }
    return MONTHS[currentMonth];
  }

  getTooltip(params: any, type: "Day" | "Week" | "Month", dateString: string) {
    const date = new Date(dateString);

    if (type === "Day") {
      return `
      ${MONTHS[date.getMonth()]} ${date.getDate()} ${date.getFullYear()}
      <br>
      <strong>${params[0].value[1]}</strong>
      `;
    } else if (type === "Week") {
      const endDate = new Date(date);
      endDate.setDate(endDate.getDate() + 7);
      return `
      Week Of:<br>
      ${MONTHS[date.getMonth()]} ${date.getDate()} ${date.getFullYear()} - ${
        MONTHS[endDate.getMonth()]
      } ${endDate.getDate()} ${endDate.getFullYear()}
      <br>
      <strong>${params[0].value[1]}</strong>
      `;
    } else if (type === "Month") {
      return `
      ${MONTHS_FULL[date.getMonth()]} ${date.getFullYear()}
      <br><strong>${params[0].value[1]}</strong>
      `;
    }
    return "";
  }
}
